import { useState, useEffect, useCallback } from 'react'
import {
  getActivities,
  getSubscriptions,
  getSubscriptionStatus,
  addSubscription,
  removeSubscription
} from '../utils/api.js'
import { getActivityEmoji, formatDateTime, hapticImpact } from '../utils/telegram.js'
import { useDelayedLoader } from '../hooks/useFetch.js'

function normalizeActivityType(type) {
  return String(type || '').toLowerCase().replace(/\s+/g, '_').replace(/-/g, '_')
}

function sanitizeVenueName(name) {
  const normalized = String(name || '').trim()
  if (!normalized) return ''
  return normalized.toLowerCase() === 'no brand' ? '' : normalized
}

function formatDateLabel(dateStr) {
  if (!dateStr) return 'Unknown date'
  try {
    const d = new Date(dateStr)
    const today = new Date()
    const tomorrow = new Date(today)
    tomorrow.setDate(today.getDate() + 1)

    const sameDay = (a, b) =>
      a.getFullYear() === b.getFullYear() &&
      a.getMonth() === b.getMonth() &&
      a.getDate() === b.getDate()

    if (sameDay(d, today)) return 'Today'
    if (sameDay(d, tomorrow)) return 'Tomorrow'
    return d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })
  } catch {
    return dateStr
  }
}

function getDateKey(dateStr) {
  if (!dateStr) return 'unknown'
  try {
    const d = new Date(dateStr)
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
  } catch {
    return 'unknown'
  }
}

function SlotsTag({ event, activityTypeKey }) {
  const typeKey = normalizeActivityType(activityTypeKey || event.type)
  if (typeKey === 'quiz' || typeKey === 'quiz_night') return null

  if (typeKey === 'ice_rink') {
    if (event.totalSlots == null) return null
    return <span className="event-slots">{event.totalSlots} slots</span>
  }

  if (event.availableSlots == null) return null
  const pct = event.totalSlots ? event.availableSlots / event.totalSlots : 1
  const cls = event.availableSlots === 0 ? 'none' : pct < 0.2 ? 'low' : ''
  return (
    <span className={`event-slots ${cls}`}>
      {event.availableSlots}/{event.totalSlots ?? '?'}
    </span>
  )
}

function EventDetailSheet({ event, activityType, onClose, onSubscriptionChange }) {
  const [eventSubscribed, setEventSubscribed] = useState(false)
  const [loading, setLoading] = useState(true)
  const eventVenueId = `event:${event.id}`
  const typeKey = normalizeActivityType(activityType || event.type)
  const hideCapacity = typeKey === 'quiz' || typeKey === 'quiz_night'

  useEffect(() => {
    setLoading(true)
    getSubscriptionStatus(eventVenueId)
      .then(res => setEventSubscribed(res.data.subscribed))
      .catch(() => setEventSubscribed(false))
      .finally(() => setLoading(false))
  }, [eventVenueId])

  const handleToggle = async () => {
    hapticImpact('medium')
    setLoading(true)
    try {
      if (eventSubscribed) {
        await removeSubscription(eventVenueId)
        setEventSubscribed(false)
        onSubscriptionChange?.()
      } else {
        await addSubscription({
          venueId: eventVenueId,
          venueName: event.name || eventVenueId,
          activityType: activityType || event.type || ''
        })
        setEventSubscribed(true)
      }
    } catch {
      // ignore
    } finally {
      setLoading(false)
    }
  }

  let slotsText = null
  if (!hideCapacity) {
    if (typeKey === 'ice_rink') {
      slotsText = event.totalSlots != null ? `${event.totalSlots}` : null
    } else if (event.availableSlots != null) {
      slotsText = `${event.availableSlots}/${event.totalSlots ?? '?'}`
    }
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-sheet" onClick={e => e.stopPropagation()}>
        <div className="modal-handle" />

        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
          <h3 style={{ fontSize: 17, fontWeight: 700, flex: 1, marginRight: 8, color: 'var(--ds-text-primary)' }}>
            {event.name || 'Event details'}
          </h3>
          <button
            style={{ background: 'none', border: 'none', color: 'var(--ds-text-secondary)', cursor: 'pointer', fontSize: 20, padding: 0 }}
            onClick={onClose}
          >×</button>
        </div>

        {/* Meta rows */}
        {event.startTime && (
          <p className="event-time">🕐 {formatDateTime(event.startTime)}{event.endTime ? ` – ${new Date(event.endTime).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}` : ''}</p>
        )}
        {event.address && <p className="event-time">📍 {event.address}</p>}
        {event.price && <p className="event-time">💶 {event.price}</p>}
        {slotsText && <p className="event-time" style={{ marginTop: 4 }}>🎟 Slots: {slotsText}</p>}
        {event.description && (
          <p style={{ fontSize: 13, color: 'var(--ds-text-secondary)', marginTop: 10, lineHeight: 1.5 }}>
            {event.description}
          </p>
        )}

        {/* Subscribe button */}
        <button
          className={`btn ${eventSubscribed ? 'btn-secondary' : 'btn-primary'}`}
          style={{ marginTop: 16 }}
          onClick={handleToggle}
          disabled={loading}
        >
          {loading ? '⏳' : eventSubscribed ? '🔕 Unsubscribe from event' : '🔔 Subscribe to event'}
        </button>
      </div>
    </div>
  )
}

export default function MyPage() {
  const [allEvents, setAllEvents] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [selectedEvent, setSelectedEvent] = useState(null)
  const showLoader = useDelayedLoader(loading)

  const loadMyEvents = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const [activitiesRes, subsRes] = await Promise.all([
        getActivities(),
        getSubscriptions()
      ])

      const grouped = activitiesRes.data?.data || {}
      const subs = subsRes.data?.data || []

      // Build sets of subscribed brand IDs and event IDs
      const brandSubIds = new Set()
      const eventSubIds = new Set()
      for (const sub of subs) {
        if (sub.is_active === false) continue
        const id = sub.venue_id
        if (String(id).startsWith('event:')) {
          eventSubIds.add(String(id).slice('event:'.length))
        } else {
          brandSubIds.add(id)
        }
      }

      if (brandSubIds.size === 0 && eventSubIds.size === 0) {
        setAllEvents([])
        setLoading(false)
        return
      }

      // Flatten all events, annotating each with its venue/brand info
      const flattened = []
      for (const [activityType, venues] of Object.entries(grouped)) {
        for (const [venueId, brandInfo] of Object.entries(venues)) {
          const venueName = sanitizeVenueName(brandInfo.venueName)
          const emoji = getActivityEmoji(activityType)
          const brandSubscribed = brandSubIds.has(venueId)

          for (const event of (brandInfo.events || [])) {
            const eventSubscribed = eventSubIds.has(String(event.id))
            if (brandSubscribed || eventSubscribed) {
              flattened.push({
                ...event,
                _venueId: venueId,
                _venueName: venueName || activityType.replace(/_/g, ' '),
                _activityType: activityType,
                _emoji: emoji,
                _brandSubscribed: brandSubscribed
              })
            }
          }
        }
      }

      // Sort by startTime ascending (soonest first)
      flattened.sort((a, b) => {
        if (!a.startTime) return 1
        if (!b.startTime) return -1
        return new Date(a.startTime) - new Date(b.startTime)
      })

      setAllEvents(flattened)
    } catch (err) {
      setError(err?.response?.data?.error || 'Failed to load your events')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { loadMyEvents() }, [loadMyEvents])

  const openEvent = useCallback((event) => {
    setSelectedEvent(event)
  }, [])

  const closeEvent = useCallback(() => setSelectedEvent(null), [])

  if (!loading && error && allEvents.length === 0) {
    return (
      <div className="status-message">
        <p>⚠️ {error}</p>
        <button className="btn btn-secondary btn-sm" style={{ marginTop: 12, width: 'auto' }} onClick={loadMyEvents}>
          Retry
        </button>
      </div>
    )
  }

  // Initial load: show spinner only after delay threshold to avoid flicker
  if (loading && allEvents.length === 0) {
    if (!showLoader) return null
    return (
      <div className="status-message">
        <div className="loading-spinner" />
        <p style={{ marginTop: 12 }}>Loading your events…</p>
      </div>
    )
  }

  if (!loading && allEvents.length === 0) {
    return (
      <div className="empty-state">
        <div className="empty-state-icon">🔔</div>
        <div className="empty-state-title">Nothing subscribed yet</div>
        <div className="empty-state-text">
          Go to the All tab, open a brand and subscribe to see upcoming events here.
        </div>
      </div>
    )
  }

  // Group by date for display
  const byDate = {}
  for (const event of allEvents) {
    const key = getDateKey(event.startTime)
    if (!byDate[key]) byDate[key] = { label: formatDateLabel(event.startTime), events: [] }
    byDate[key].events.push(event)
  }

  return (
    <div className="page-content">
      <div className="page-header">
        <div className="page-header-title">
          My Events
          {showLoader && <span className="inline-loader" aria-label="Refreshing" />}
        </div>
        <div className="page-header-subtitle">
          {allEvents.length} upcoming event{allEvents.length !== 1 ? 's' : ''}
        </div>
      </div>

      {Object.entries(byDate).map(([key, { label, events }]) => (
        <div key={key}>
          <div className="date-chip">{label}</div>
          {events.map(event => (
            <div
              key={`${event._venueId}-${event.id}`}
              className="event-list-item"
              onClick={() => openEvent(event)}
            >
              <div style={{ fontSize: 20, flex: '0 0 auto', lineHeight: 1 }}>{event._emoji}</div>
              <div className="event-list-item-body">
                <div className="event-list-item-title">{event.name}</div>
                <div className="event-list-item-brand">{event._venueName}</div>
                {event.startTime && (
                  <div className="event-list-item-meta">
                    🕐 {formatDateTime(event.startTime)}
                    {event.address ? ` · 📍 ${event.address}` : ''}
                  </div>
                )}
              </div>
              <div className="event-list-item-right">
                <SlotsTag event={event} activityTypeKey={event._activityType} />
              </div>
            </div>
          ))}
        </div>
      ))}

      <button
        className="btn btn-secondary"
        style={{ marginTop: 8 }}
        onClick={loadMyEvents}
        disabled={loading}
      >
        ↻ Refresh
      </button>

      {selectedEvent && (
        <EventDetailSheet
          event={selectedEvent}
          activityType={selectedEvent._activityType}
          onClose={closeEvent}
          onSubscriptionChange={loadMyEvents}
        />
      )}
    </div>
  )
}
